if (!customElements.get('announcement-popup')) {
  customElements.define(
    'announcement-popup',
    class AnnouncementPopup extends DrawerComponent {
      static get observedAttributes() {
        return super.observedAttributes.concat(['data-delay', 'data-reappear-days']);
      }

      constructor() {
        super();
        
        // Performance optimization: bind methods once
        this.handleEscapeKey = this.handleEscapeKey.bind(this);
        this.handleClickOutside = this.handleClickOutside.bind(this);
        this.handleResize = this.handleResize.bind(this);
        
        // Throttled resize handler for performance
        this.throttledResize = this.throttle(this.handleResize, 100);
      }

      get requiresBodyAppended() {
        return false;
      }

      connectedCallback() {
        super.connectedCallback();

        this.delay = parseInt(this.dataset.delay) || 3;
        this.reappearDays = parseInt(this.dataset.reappearDays) || 7;
        this.cookieName = 'hypertheme:announcement-popup';
        this.isShown = false;
        this.timeoutId = null;

        // Core Web Vitals optimization: defer initialization
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => {
            this.initialize();
          });
        } else {
          this.initialize();
        }
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanup();
      }

      initialize() {
        // Performance: use requestIdleCallback if available
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            this.setupEventListeners();
            this.handleShow();
          });
        } else {
          setTimeout(() => {
            this.setupEventListeners();
            this.handleShow();
          }, 0);
        }
      }

      setupEventListeners() {
        // Close button - use capture phase to handle before parent
        const closeBtn = this.querySelector('.announcement-popup__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopImmediatePropagation(); // Stop all other listeners
            this.closePopup();
          }, true); // Use capture phase
        }

        // Popup button - save closed state when clicked
        const popupBtn = this.querySelector('.announcement-popup__custom-btn');
        if (popupBtn) {
          popupBtn.addEventListener('click', (e) => {
            // Don't prevent default - allow navigation to proceed
            // But save the closed state so popup doesn't reappear
            this.saveClosedState();
          });
        }

        // Overlay click - block all interactions with background elements
        const overlay = this.querySelector('.announcement-popup__overlay');
        if (overlay) {
          overlay.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Don't close on overlay click as per requirements
            // But ensure the overlay blocks all background interactions
          });
          
          // Block all pointer events on overlay to prevent background interactions
          overlay.addEventListener('mousedown', (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
          
          overlay.addEventListener('touchstart', (e) => {
            e.preventDefault();
            e.stopPropagation();
          });
        }

        // Ensure popup container allows interactions
        const container = this.querySelector('.announcement-popup__container');
        if (container) {
          container.addEventListener('click', (e) => {
            // Allow clicks within the popup container
            e.stopPropagation();
          });
        }

        // Escape key handler
        document.addEventListener('keydown', this.handleEscapeKey);
        
        // Resize handler for responsive behavior
        window.addEventListener('resize', this.throttledResize);
      }

      cleanup() {
        if (this.timeoutId) {
          clearTimeout(this.timeoutId);
          this.timeoutId = null;
        }
        
        document.removeEventListener('keydown', this.handleEscapeKey);
        window.removeEventListener('resize', this.throttledResize);
        
        // Remove body scroll lock and restore scrolling
        document.body.classList.remove('announcement-popup-open');
        
        // Remove modal classes that might be added by parent ModalComponent
        document.body.classList.remove('modal-show', 'modal-showing', 'modal-open');
        
        // Force restore body scroll styles
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('padding-right');
      }

      handleEscapeKey(event) {
        if (event.key === 'Escape' && this.isShown) {
          this.closePopup();
        }
      }

      closePopup() {
        if (!this.isShown) return;
        
        // Set aria-hidden to true
        this.setAttribute('aria-hidden', 'true');
        this.isShown = false;
        
        // Remove body scroll lock and restore scrolling
        document.body.classList.remove('announcement-popup-open');
        
        // Remove modal classes that might be added by parent ModalComponent
        document.body.classList.remove('modal-show', 'modal-showing', 'modal-open');
        
        // Force restore body scroll styles (in case class removal isn't enough)
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('padding-right');
        
        // Save closed state
        this.saveClosedState();
        
        // Return focus
        this.returnFocus();
        
        // Try to call parent hide method if available
        if (typeof super.hide === 'function') {
          try {
            super.hide();
          } catch (error) {
            // Silent fallback - parent method failed but popup still closed
          }
        }
      }

      handleClickOutside(event) {
        // Not used - popup only closes via X button
      }

      handleResize() {
        if (this.isShown) {
          // Adjust popup positioning on resize if needed
          this.adjustPosition();
        }
      }

      adjustPosition() {
        // Performance optimization: use transform instead of changing layout properties
        const container = this.querySelector('.announcement-popup__container');
        if (container) {
          // Reset any transforms to recalculate center position
          requestAnimationFrame(() => {
            container.style.transform = '';
            // Re-center if needed
          });
        }
      }

      handleShow() {
        if (Shopify.designMode) return;

        // Check if popup should be shown
        const savedData = FoxTheme.utils.getStorage(this.cookieName);
        if (savedData && savedData.closed) {
          const now = new Date().getTime();
          const closedTime = new Date(savedData.timestamp).getTime();
          const daysPassed = (now - closedTime) / (1000 * 60 * 60 * 24);
          
          if (daysPassed < this.reappearDays) {
            return false;
          }
        }

        // Show popup after delay
        if (this.delay > 0) {
          this.timeoutId = setTimeout(() => {
            this.show();
          }, this.delay * 1000);
        } else {
          this.show();
        }
      }

      prepareToShow() {
        super.prepareToShow();
        
        // Performance: prevent layout shifts
        this.setAttribute('data-loading', 'true');
        
        // Force layout calculation before showing
        requestAnimationFrame(() => {
          this.removeAttribute('data-loading');
          this.setAttribute('aria-hidden', 'false');
          this.isShown = true;
          
          // Lock body scroll
          document.body.classList.add('announcement-popup-open');
          
          // Focus management for accessibility
          this.focusFirstElement();
        });
      }

      prepareToHide() {
        super.prepareToHide();
        
        this.setAttribute('aria-hidden', 'true');
        this.isShown = false;
        
        // Remove body scroll lock
        document.body.classList.remove('announcement-popup-open');
        
        // Save closed state
        this.saveClosedState();
        
        // Return focus to trigger element if it exists
        this.returnFocus();
      }

      focusFirstElement() {
        // Focus the close button for keyboard navigation
        const closeBtn = this.querySelector('.announcement-popup__close');
        if (closeBtn) {
          closeBtn.focus();
        }
      }

      returnFocus() {
        // Return focus to body or previously focused element
        if (document.activeElement && document.activeElement !== document.body) {
          document.activeElement.blur();
        }
      }

      saveClosedState() {
        if (Shopify.designMode) return;

        const data = {
          closed: true,
          timestamp: new Date().toISOString()
        };
        
        FoxTheme.utils.setStorage(this.cookieName, data, this.reappearDays);
      }

      // Utility function for throttling events
      throttle(func, limit) {
        let inThrottle;
        return function() {
          const args = arguments;
          const context = this;
          if (!inThrottle) {
            func.apply(context, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
          }
        }
      }

      // Override show method for performance optimizations
      show() {
        if (this.isShown) return;
        
        // Performance: use CSS containment
        this.style.contain = 'layout style paint';
        
        const result = super.show();
        
        // Remove conflicting modal classes that parent might have added
        // We use our own announcement-popup-open class instead
        document.body.classList.remove('modal-show', 'modal-showing', 'modal-open');
        
        return result;
      }

      // Override hide method for performance optimizations
      hide() {
        if (!this.isShown) return;
        
        // Ensure scroll restoration before calling parent
        document.body.classList.remove('announcement-popup-open');
        
        // Remove modal classes that might be added by parent ModalComponent
        document.body.classList.remove('modal-show', 'modal-showing', 'modal-open');
        
        // Force restore body scroll styles
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('width');
        document.body.style.removeProperty('padding-right');
        
        return super.hide();
      }

      // Override parent's button click handling
      onButtonClick(event) {
        // Check if this is our close button
        if (event.currentTarget.classList.contains('announcement-popup__close')) {
          event.preventDefault();
          event.stopPropagation();
          this.closePopup();
          return;
        }
        
        // For other buttons, prevent parent's default behavior
        event.preventDefault();
        event.stopPropagation();
        
        // Don't call super.onButtonClick as it interferes with our logic
      }

      // Handle attribute changes
      attributeChangedCallback(name, oldValue, newValue) {
        super.attributeChangedCallback(name, oldValue, newValue);
        
        if (name === 'data-delay') {
          this.delay = parseInt(newValue) || 3;
        } else if (name === 'data-reappear-days') {
          this.reappearDays = parseInt(newValue) || 7;
        }
      }

      // Performance monitoring for Core Web Vitals
      measurePerformance() {
        if ('performance' in window && performance.mark) {
          performance.mark('announcement-popup-show-start');
          
          requestAnimationFrame(() => {
            performance.mark('announcement-popup-show-end');
            performance.measure(
              'announcement-popup-show',
              'announcement-popup-show-start',
              'announcement-popup-show-end'
            );
          });
        }
      }

      // Intersection Observer for visibility optimization
      setupVisibilityObserver() {
        if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                // Popup is visible - can perform expensive operations
                this.onVisible();
              } else {
                // Popup is not visible - pause expensive operations
                this.onHidden();
              }
            });
          });
          
          observer.observe(this);
          this.visibilityObserver = observer;
        }
      }

      onVisible() {
        // Placeholder for when popup becomes visible
      }

      onHidden() {
        // Placeholder for when popup becomes hidden
      }
    }
  );
}

// Performance optimization: preload critical resources
if ('requestIdleCallback' in window) {
  requestIdleCallback(() => {
    // Preload any critical resources for the popup
    if (document.querySelector('announcement-popup')) {
      // Popup exists, can preload resources
    }
  });
}
